import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Component, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {PDFDocumentProxy} from 'ng2-pdf-viewer';
import {finalize} from 'rxjs/operators';
import {ApiService} from '../../../services/api.service';
import {TemplateService} from '../../../services/template.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-document-issue',
  templateUrl: './document-issue.component.html',
  styleUrls: ['./document-issue.component.scss'],
  providers: [TemplateService]
})
export class DocumentIssueComponent implements OnInit {
  username = localStorage.getItem('username');

  buyerBranchID: string;
  documentTypes = this.template.get().filter(doc =>
    doc.type !== 'tax_invoice' && doc.type !== 'invoice_tax_invoice' &&
    doc.type !== 'delivery_order_tax_invoice' && doc.type !== 'receipt' &&
    doc.type !== 'invoice'
  );
  selectedDocumentType: any;

  @Output() docProcessType: any;

  step = 1;

  pdf: any;
  pdfPage = 1;
  pdfPageLength: number;
  pdfError: string;
  pdfLoaded = false;
  pdfPath: string;

  documentFormData: any;
  isLoading = false;
  documentInformation: any;
  abbData: any;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private apiService: ApiService,
    private template: TemplateService,
    private translate: TranslateService,
    private router: Router
  ) {
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.buyerBranchID = params['buyer_branch'];
    });
    this.route.queryParams.subscribe( query => {
      if (query.id) {
        if (query.type === 'reissue') {
          this.docProcessType = 'reissue';
          this.getDocumentTaxDataInfo(query.id);
        } else if (query.type === 'request') {
          this.docProcessType = 'request';
          this.getDocumentRequestInfo(query.id);
        }
      }

      if (query.abbData) {
        this.getAbbDetail(query.abbData);
      }
    });
  }

  getAbbDetail(id: number): void {
    this.apiService.getAbbDetail(id).subscribe((response) => {
      this.abbData = response;
      const foundTemplate = this.template.get().find( item => item.type === 'receipt_tax_invoice');
      this.selectDocumentType(foundTemplate);
    });
  }

  getDocumentRequestInfo(id: number): void {
    if (!id) {
      return;
    }
    this.apiService.getDocumentRequestInfo(id).subscribe( res => {
      this.documentInformation = res;
      const foundTemplate = this.template.get().find( item => item.type === 'receipt_tax_invoice');
      this.selectDocumentType(foundTemplate);
    });
  }

  getDocumentTaxDataInfo(id: number): void {
    this.apiService.getDocumentTaxDataInfo( { id: id }).subscribe( res => {
      this.documentInformation = res.results[0];
      const foundTemplate = this.template.get().find( item => item.type === res.results[0].rd_document_type);
      this.selectDocumentType(foundTemplate);
    });
  }

  selectDocumentType(type) {
    this.selectedDocumentType = type;
    this.step = 2;
  }

  setPDF(pdf: PDFDocumentProxy) {
    this.pdfPageLength = pdf.numPages;
    this.pdfLoaded = true;
  }

  resetPDF() {
    this.pdfLoaded = false;
    this.pdfPage = 1;
    this.pdfError = undefined;
  }

  nextPage() {
    if (this.pdfPage < this.pdfPageLength) {
      this.pdfPage++;
    }
  }

  prevPage() {
    if (this.pdfPage > 1) {
      this.pdfPage--;
    }
  }

  createETaxInvoice() {
    this.isLoading = true;

    this.resetPDF();

    this.apiService.createETaxInvoice(this.documentFormData)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res: any) => {
          this.pdfPath = res.pdf_file;
          this.pdf = {
            url: res.pdf_file,
            httpHeaders: {Authorization: 'Bearer' + ' ' + localStorage.getItem('currentUser')},
            withCredentials: true
          };

          if (!this.documentFormData.preview) {
            this.sniff('CREATE_DOC', this.username);
          }
        },
        (err: any) => {
          const duplicate_branch_name = 'duplicate key value violates unique constraint';
          const errorDetail = err.error;

          if (errorDetail.includes(duplicate_branch_name)) {
            if (errorDetail.includes('tax_data_taxdata_abb_data_id_key')) {
              this.pdfError = this.translate.instant('ISSUE-TEXT.DUPLICATE-ABB');
            } else {
              const detailIndex: number = errorDetail.indexOf('DETAIL:');
              if (detailIndex !== -1) {
                  const charactersAfterDetail: string = errorDetail.substring(detailIndex + 'DETAIL:'.length);
                  this.pdfError = charactersAfterDetail.trim();
              }
            }
          } else {
            this.pdfError = err.error;
          }
        }
      );
  }

  previewPDF(data: any) {
    this.documentFormData = data;

    if (this.documentFormData) {
      this.step = 3;
      this.documentFormData['preview'] = true;

      this.createETaxInvoice();
    } else {
      this.removeQueryParam();
      this.documentFormData = null;
      this.step = 1;
    }
  }

  removeQueryParam() {
    const urlTree = this.router.parseUrl(this.router.url);
    delete urlTree.queryParams['id'];
    delete urlTree.queryParams['type'];
    this.router.navigateByUrl(urlTree);
  }

  editDoc() {
    this.step = 2;
    this.pdf = undefined;
    window.scrollTo(0, 0);
  }

  submitDoc() {
    this.step = 4;

    this.documentFormData['preview'] = false;

    this.createETaxInvoice();
  }

  startOver() {
    this.step = 1;
    this.pdf = undefined;
  }

  print() {
    this.apiService.getFileFullPath(this.pdfPath).subscribe(
      (blob: Blob) => {
        const rdDocType = this.documentFormData.file_type;
        const invoiceNumber = this.documentFormData.invoice_number;
        const issueDt = this.documentFormData.issue_dt.slice(0, 10).replace(/-/gi, '_');
        const fileName = `${invoiceNumber}_${rdDocType}_${issueDt}.pdf`;
        this.apiService.createDownloadFile(blob, fileName);
        this.sniff('CLICK_PRINT', this.documentFormData.invoice_number);
      },
      (error: HttpErrorResponse) => {
        console.log(error);
      }
    );
  }

  sniff(action: string, target: string) {
    const data = {
      action: action,
      address: 'DOC_ISSUE',
      target: target
    };
    this.apiService.sniff(data).subscribe();
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/document-issue.component.ts